import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";

const EMPTY = { name: "", onboarding_agent_id: "", onboarding_date: "2026-06-01", is_new: true, phone: "", credit_days: 30, trn: "", address: "", contact_person: "", email: "" };

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/customers").then((r) => setCustomers(r.data));
  useEffect(() => {
    load();
    api.get("/admin/agents").then((r) => setAgents(r.data));
  }, []);

  const agentName = (id) => agents.find((a) => a.id === id)?.name || "—";

  const save = async () => {
    if (!form.onboarding_agent_id) return toast.error("Select onboarding agent");
    setSaving(true);
    try {
      await api.post("/admin/customers", { ...form, credit_days: Number(form.credit_days || 30) });
      toast.success("Customer added");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="Record onboarding agent and date — drives the 6-month account ownership window."
        actions={
          <Button onClick={() => setOpen(true)} data-testid="add-customer-button">
            <Plus className="w-4 h-4 mr-1" /> New customer
          </Button>
        }
      />
      {!customers ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : customers.length === 0 ? (
        <Empty>No customers yet.</Empty>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium px-4 py-3">Customer</th>
                  <th className="text-left font-medium px-4 py-3">Onboarded by</th>
                  <th className="text-left font-medium px-4 py-3">Date</th>
                  <th className="text-left font-medium px-4 py-3">Type</th>
                  <th className="text-right font-medium px-4 py-3">Credit days</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-b border-border/50 hover:bg-secondary/40">
                    <td className="px-4 py-3 font-medium"><Link to={`/dashboard/customers/${c.id}`} className="text-primary hover:underline" data-testid={`customer-link-${c.id}`}>{c.name}</Link><div className="text-xs text-muted-foreground">{[c.phone, c.trn ? `TRN ${c.trn}` : null].filter(Boolean).join(" · ")}</div></td>
                    <td className="px-4 py-3 text-muted-foreground">{agentName(c.onboarding_agent_id)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.onboarding_date}</td>
                    <td className="px-4 py-3">
                      <Badge variant={c.is_new ? "default" : "secondary"}>{c.is_new ? "New" : "Repeat"}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground" data-testid={`customer-credit-days-${c.id}`}>{c.credit_days ?? 30}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Onboarding agent</Label>
              <Select value={form.onboarding_agent_id} onValueChange={(v) => setForm({ ...form, onboarding_agent_id: v })}>
                <SelectTrigger data-testid="customer-agent-select"><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent>
                  {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Onboarding date</Label>
                <Input type="date" value={form.onboarding_date} onChange={(e) => setForm({ ...form, onboarding_date: e.target.value })} data-testid="customer-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">TRN (VAT no.)</Label>
                <Input value={form.trn} onChange={(e) => setForm({ ...form, trn: e.target.value })} data-testid="customer-trn-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Contact person</Label>
                <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="customer-contact-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Billing address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="customer-address-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Credit period (days) - set per customer payment track record</Label>
              <Input type="number" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: e.target.value })} data-testid="customer-credit-days-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="save-customer-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
